import React from 'react';
import { motion } from "framer-motion";
import { UnifiedProjectsComponent } from '@/components/UnifiedProjects';
import SkillCategoriesDisplay from "@/components/skills/components/SkillCategoriesDisplay";
import { useSkillsState } from '@/lib/skills';

export default function Portfolio() {
  // Use centralized skill state management
  const {
    badgeSelection,
    currentSkills,
    isLoading,
    updateBadgeSelection,
    enhancedProjects,
  } = useSkillsState();

  // Determine calculation type for display
  const calculationType = React.useMemo(() => {
    if (badgeSelection.showProfessional && badgeSelection.showPrivate) {
      return 'combined' as const;
    } else if (badgeSelection.showPrivate) {
      return 'private' as const;
    } else {
      return 'professional' as const;
    }
  }, [badgeSelection.showProfessional, badgeSelection.showPrivate]);

  // Handle badge selection changes from UnifiedProjectsComponent
  const handleBadgeSelectionChange = (newShowProfessional: boolean, newShowPrivate: boolean) => {
    updateBadgeSelection({
      showProfessional: newShowProfessional,
      showPrivate: newShowPrivate,
    });
  };

  return (
    <main className="container mx-auto p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Header Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Skills & Projects
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore my professional expertise and personal projects. Skills are enhanced with GitHub language analysis.
          </p>
        </motion.section>

        {/* Skills Overview Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-lg p-6 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">Skills Overview</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Calculation Type:</span>
              <span className="text-sm font-medium capitalize">{calculationType}</span>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-muted-foreground">Loading skills...</span>
              </div>
            </div>
          ) : currentSkills && Object.keys(currentSkills).length > 0 ? (
            <SkillCategoriesDisplay 
              skillsByCategory={currentSkills}
              calculationType={calculationType}
              enhancedProjects={enhancedProjects}
            />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No skills data available for current selection.</p>
            </div>
          )}
        </motion.section>

        {/* Projects Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <UnifiedProjectsComponent
            showPrivateProjects={badgeSelection.showPrivate}
            defaultViewMode="list"
            showCompanyLogos={true}
            pageTitle="Portfolio Projects"
            emptyStateMessage="No projects found."
            onBadgeSelectionChange={handleBadgeSelectionChange}
          />
        </motion.section>
      </motion.div>
    </main>
  );
}
